import React, { useState } from 'react';
import { BrainCircuit, Loader2, Sparkles } from 'lucide-react';

import AIConfidenceIndicator from './AIConfidenceIndicator';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const AICopilotWorkbench = ({ config, onSubmit, variant = 'dark' }) => {
  const [query, setQuery] = useState(config?.suggestions?.[0] || '');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const isLight = variant === 'light';

  const handleRun = async (nextQuery = query) => {
    if (!nextQuery?.trim()) return;
    try {
      setLoading(true);
      const response = await onSubmit(nextQuery);
      setResult(response);
      setQuery(nextQuery);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={isLight ? 'rounded-3xl border border-border bg-card p-5 text-foreground shadow-sm' : 'rounded-3xl border border-slate-700/80 bg-slate-950/90 p-5 text-slate-100 shadow-[0_18px_60px_rgba(2,6,23,0.36)] backdrop-blur-sm'}>
      <div className="flex items-center gap-3">
        <div className={isLight ? 'rounded-xl border border-primary/20 bg-primary/10 p-2.5' : 'rounded-xl border border-cyan-400/20 bg-cyan-400/10 p-2.5'}>
          <BrainCircuit className={`h-4 w-4 ${isLight ? 'text-primary' : 'text-cyan-200'}`} />
        </div>
        <div>
          <p className={`text-sm font-semibold ${isLight ? 'text-foreground' : 'text-slate-50'}`}>{config?.title || 'AI HR Copilot'}</p>
          <p className={`text-xs ${isLight ? 'text-muted-foreground' : 'text-slate-300'}`}>{config?.description}</p>
        </div>
      </div>

      <div className="mt-5 flex gap-3">
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Ask workforce intelligence questions"
          className={isLight ? 'border-border bg-card text-foreground placeholder:text-muted-foreground' : 'border-slate-700 bg-slate-900/95 text-slate-100 placeholder:text-slate-400'}
        />
        <Button onClick={() => handleRun()} className="hover-lift bg-cyan-500 text-slate-950 hover:bg-cyan-400">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Run'}
        </Button>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {(config?.suggestions || []).map((suggestion) => (
          <button
            key={suggestion}
            type="button"
            onClick={() => handleRun(suggestion)}
            className={isLight ? 'rounded-full border border-border bg-muted px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:bg-primary/10 hover:text-primary' : 'rounded-full border border-slate-700 bg-slate-900/80 px-3 py-1.5 text-xs text-slate-200 transition-colors hover:border-cyan-400/40 hover:bg-cyan-500/12 hover:text-cyan-100'}
          >
            {suggestion}
          </button>
        ))}
      </div>

      {result && (
        <div className={isLight ? 'mt-5 space-y-4 rounded-2xl border border-border bg-muted p-4' : 'mt-5 space-y-4 rounded-2xl border border-slate-700/80 bg-slate-900/95 p-4'}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className={`text-sm font-semibold ${isLight ? 'text-foreground' : 'text-slate-50'}`}>Structured Response</p>
              <p className={`mt-1 text-sm ${isLight ? 'text-foreground' : 'text-slate-200'}`}>{result.summary}</p>
            </div>
            <Sparkles className={`h-4 w-4 ${isLight ? 'text-primary' : 'text-cyan-200'}`} />
          </div>
          <AIConfidenceIndicator value={result.confidence_score} variant={variant} />
          <div className="grid gap-3 sm:grid-cols-3">
            {(result.key_metrics || []).map((metric) => (
              <div key={metric.label} className={isLight ? 'rounded-xl border border-border bg-card p-3' : 'rounded-xl border border-slate-700/80 bg-slate-800/70 p-3'}>
                <p className={`text-[11px] uppercase tracking-[0.18em] ${isLight ? 'text-muted-foreground' : 'text-slate-300'}`}>{metric.label}</p>
                <p className={`mt-2 text-lg font-semibold ${isLight ? 'text-foreground' : 'text-slate-50'}`}>{metric.value}</p>
              </div>
            ))}
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <div>
              <p className={`text-xs uppercase tracking-[0.18em] ${isLight ? 'text-muted-foreground' : 'text-slate-300'}`}>Recommendations</p>
              <div className="mt-3 space-y-2">
                {(result.recommendations || []).map((item) => (
                  <div key={item} className={isLight ? 'rounded-xl border border-border bg-card p-3 text-sm text-foreground' : 'rounded-xl border border-slate-700/80 bg-slate-800/70 p-3 text-sm text-slate-100'}>
                    {item}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className={`text-xs uppercase tracking-[0.18em] ${isLight ? 'text-muted-foreground' : 'text-slate-300'}`}>Explainability</p>
              <div className="mt-3 space-y-2">
                {(result.explainability || []).map((item) => (
                  <div key={item} className={isLight ? 'rounded-xl border border-border bg-card p-3 text-sm text-foreground' : 'rounded-xl border border-slate-700/80 bg-slate-800/70 p-3 text-sm text-slate-100'}>
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AICopilotWorkbench;
